"use client";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import axiosInstance from "../utlis/axiosInstance";
import moment from "moment";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Slider,
  Divider,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Tooltip,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Traffic as TrafficIcon,
  Terrain as TerrainIcon,
  Satellite as SatelliteIcon,
  Map as MapIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
// Google Maps imports
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

// Google Maps constants
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Constants for playback
const speeds = [1, 2, 3, 4, 5, 6];
const MAX_POINTS = 5000;

// Interface for history points based on API response
interface HistoryPoint {
  timestamp: Date;
  location: {
    lat: number;
    lng: number;
    address?: string;
  };
  speed: number;
  heading: number;
  status: string;
  latitude: number;
  longitude: number;
  fix_time: string;
  created_at: string;
  altitude?: number;
  course?: number;
  attributes?: {
    latest_speed?: number;
    battery?: number;
  };
}

// Interface for stop locations
interface StopLocation {
  startTime: Date;
  endTime: Date;
  latitude: number;
  longitude: number;
  duration: number;
  battery?: number;
  gpsInfo?: string;
  courseStatus?: string;
}

// Interface for summary data
interface SummaryData {
  distance: number;
  duration: number;
  stops: number;
  maxSpeed: number;
  avgSpeed: number;
  ignitionHours: number;
  idleTime: number;
  stopTime: number;
  chargingTime: number;
  gpsTrackingTime: number;
  startOdometer: number;
  endOdometer: number;
  startPosition: string;
  endPosition: string;
  currentStatus: string;
  batteryLevel: number;
  avgBatteryLevel: number;
  voltageLevel: number;
  gsmSignal: number;
}

// Create styled components for Grid to avoid the component property requirement
const StyledGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
}));

const HistoryPlayback: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Get vehicle name from navigation state or use a fallback
  const vehicleName =
    (location.state as { vehicleName?: string })?.vehicleName ||
    `Vehicle ${id}`;

  // Map refs similar to VehicleTracking
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const vehicleMarkerRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const stopMarkersRef = useRef<any[]>([]);
  const startMarkerRef = useRef<any>(null);
  const endMarkerRef = useRef<any>(null);
  const trafficLayerRef = useRef<any>(null);

  // State
  const [historyData, setHistoryData] = useState<HistoryPoint[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [stopLocations, setStopLocations] = useState<StopLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [playing, setPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: 37.7749,
    lng: -122.4194,
  });
  const [mapZoom, setMapZoom] = useState(13);
  const [showStops, setShowStops] = useState(true);
  const [showTraffic, setShowTraffic] = useState(false);
  const [mapType, setMapType] = useState<
    "roadmap" | "satellite" | "terrain" | "hybrid"
  >("roadmap");
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date()); // Current time

  // Helper function to fetch data with explicit dates
  const fetchDataWithDates = useCallback(async (fromDate: Date | null, toDate: Date | null) => {
    if (!id || !fromDate || !toDate) return;

    try {
      setLoading(true);
      // Match mobile app time calculation: subtract 5 hours 30 minutes (UTC to IST offset)
      const fromStr = moment(fromDate)
      .subtract(5, "hours")
      .subtract(30, "minutes")
      .format("YYYY-MM-DD HH:mm:ss");

    const toStr = moment(toDate)
      .subtract(5, "hours")
      .subtract(30, "minutes")
      .format("YYYY-MM-DD HH:mm:ss");

      // Fetch all data in parallel using Promise.all
      // Fetch stops data - COMMENTED OUT: No longer using stops API
      // const stopsResponse = await axiosInstance.get(
      //   `/gpstracker/device/${id}/report?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}&report=stops`
      // );

      const [positionsResponse, summaryResponse] = await Promise.all([
        axiosInstance.get(
          `/gpstracker/positionhandler/?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}&device_id=${id}`
        ),
        axiosInstance.get(
          `/gpstracker/device/${id}/report?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}&report=summary`
        ),
      ]);

      // Debug logs can be uncommented for debugging
      // console.log("summaryResponse", summaryResponse);
      // console.log("positionsResponse", positionsResponse);
      // console.log("stopsResponse", stopsResponse);

      // Process positions data
      let stopsCount = 0;
      if (
        positionsResponse.data.status &&
        positionsResponse.data.responseData?.positions
      ) {
        const positions = positionsResponse.data.responseData.positions.reverse();
        const processedData = positions.map((pos: any) => ({
          timestamp: new Date(pos.fix_time),
          location: {
            lat: pos.latitude,
            lng: pos.longitude,
            address: pos.address || "N/A",
          },
          speed: pos.speed || 0,
          heading: pos.course || 0,
          status: (pos.speed || 0) > 0 ? "moving" : "idle",
          latitude: pos.latitude,
          longitude: pos.longitude,
          fix_time: pos.fix_time,
          created_at: pos.created_at,
          altitude: pos.altitude,
          course: pos.course,
          attributes: pos.attributes,
        }));
        setHistoryData(processedData);
        setCurrentIndex(0); // Start from the beginning (oldest position)

        // Debug log can be uncommented for debugging
        // console.log("Processed history data:", processedData.length, "points");

        // Set initial map center
        if (processedData.length > 0) {
          setMapCenter({
            lat: processedData[0].latitude,
            lng: processedData[0].longitude,
          });
        }

        // Calculate stops from processed history data (consecutive points with speed 0)
        // COMMENTED OUT: No longer using stops API, calculating from history data instead
        // if (stopsResponse?.data?.status && stopsResponse?.data?.responseData) {
        //   const stops = stopsResponse.data.responseData.map((stop: any) => ({
        //     startTime: new Date(stop.start_time),
        //     endTime: new Date(stop.end_time),
        //     latitude: stop.start_lat,
        //     longitude: stop.start_lon,
        //     duration: stop.duration * 3600000, // Convert to milliseconds
        //     battery: stop.battery_level,
        //     gpsInfo: stop.gps_info,
        //     courseStatus: stop.course_status,
        //   }));
        //   stopsCount = stops.length;
        //   setStopLocations(stops);
        // }
        
        // Calculate stops count from history data (consecutive points with speed 0)
        if (processedData.length > 0) {
          let stopCount = 0;
          const MIN_STOP_DURATION = 60000; // 1 minute in milliseconds
          
          for (let i = 0; i < processedData.length; i++) {
            const currentPoint = processedData[i];
            const isStopped = currentPoint.speed === 0;
            
            if (isStopped) {
              // Start of a potential stop
              const stopStartTime = currentPoint.timestamp.getTime();
              
              // Check if this stop lasts long enough
              let j = i + 1;
              while (j < processedData.length && processedData[j].speed === 0) {
                j++;
              }
              
              if (j <= processedData.length) {
                const stopEndTime = processedData[j - 1].timestamp.getTime();
                const stopDuration = stopEndTime - stopStartTime;
                
                if (stopDuration >= MIN_STOP_DURATION) {
                  stopCount++;
                }
              }
              
              i = j - 1; // Skip ahead
            }
          }
          
          stopsCount = stopCount;
        }
      }
      
      // Set empty stop locations since we're not using the API
      setStopLocations([]);

      // Process summary data - same structure as VehicleTracking.tsx
      if (summaryResponse?.data?.responseData) {
        const responseData = summaryResponse.data.responseData;
        
        // Extract device data - structure: responseData[deviceId][date]
        const deviceIdStr = id?.toString() || "";
        const deviceData = responseData[deviceIdStr];
        
        if (deviceData) {
          // Get the date range - aggregate data across all dates in the range
          const startDateStr = moment(fromDate).format("YYYY-MM-DD");
          const endDateStr = moment(toDate).format("YYYY-MM-DD");
          
          // Get all dates within the selected range
          const datesInRange = Object.keys(deviceData)
            .filter(date => date >= startDateStr && date <= endDateStr)
            .sort();
          
          // Parse time strings - they come as formatted strings like "2h 30m 0s"
          const parseTimeString = (timeStr: string | number): number => {
            if (typeof timeStr === 'number') {
              return timeStr * 3600000; // Convert hours to milliseconds
            }
            if (!timeStr || typeof timeStr !== 'string') return 0;
            
            // Parse format like "2h 30m 0s" or "2h 30m"
            const hoursMatch = timeStr.match(/(\d+)h/);
            const minutesMatch = timeStr.match(/(\d+)m/);
            const secondsMatch = timeStr.match(/(\d+)s/);
            
            const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
            const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
            const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;
            
            return (hours * 3600 + minutes * 60 + seconds) * 1000; // Convert to milliseconds
          };

          // Convert milliseconds to hours for calculation
          const millisecondsToHours = (ms: number): number => {
            return ms / (1000 * 60 * 60);
          };
          
          if (datesInRange.length > 0) {
            // Aggregate data across all dates in range
            let totalDistance = 0; // in km
            let maxSpeed = 0;
            let totalRunningTimeMs = 0; // in milliseconds
            
            // Get data from the first and last dates for other fields
            const firstDateData = deviceData[datesInRange[0]];
            const lastDateData = deviceData[datesInRange[datesInRange.length - 1]];
            
            // Aggregate across all dates
            datesInRange.forEach(date => {
              const dateData = deviceData[date];
              if (dateData) {
                // Sum total_distance
                totalDistance += dateData.total_distance || 0;
                
                // Find max max_speed
                if (dateData.max_speed && dateData.max_speed > maxSpeed) {
                  maxSpeed = dateData.max_speed;
                }
                
                // Sum running_time for weighted average calculation
                totalRunningTimeMs += parseTimeString(dateData.running_time || "0h 0m 0s");
              }
            });
            
            // Calculate weighted average speed: total distance / total running time (in hours)
            const totalRunningTimeHours = millisecondsToHours(totalRunningTimeMs);
            const avgSpeed = totalRunningTimeHours > 0 
              ? totalDistance / totalRunningTimeHours 
              : 0;
            
            const processedSummary = {
              distance: totalDistance * 1000, // Convert km to meters
              duration: totalRunningTimeMs,
              stops: stopsCount, // Use stops count from stops response
              maxSpeed: maxSpeed,
              avgSpeed: avgSpeed,
              ignitionHours: parseTimeString(lastDateData?.ignition_hours || "0h 0m 0s"),
              idleTime: parseTimeString(lastDateData?.idle_time || "0h 0m 0s"),
              stopTime: parseTimeString(lastDateData?.stop_time || "0h 0m 0s"),
              chargingTime: parseTimeString(lastDateData?.charging_time || "0h 0m 0s"),
              gpsTrackingTime: parseTimeString(lastDateData?.gps_tracking_time || "0h 0m 0s"),
              startOdometer: firstDateData?.start_odometer || 0,
              endOdometer: lastDateData?.end_odometer || 0,
              startPosition: typeof firstDateData?.start_position === 'string' 
                ? firstDateData.start_position 
                : firstDateData?.start_position?.address || "",
              endPosition: typeof lastDateData?.end_position === 'string'
                ? lastDateData.end_position
                : lastDateData?.end_position?.address || "",
              currentStatus: lastDateData?.current_status || "",
              batteryLevel: lastDateData?.current_battery_level || 0,
              avgBatteryLevel: lastDateData?.average_battery_level || 0,
              voltageLevel: lastDateData?.most_common_voltage || 0,
              gsmSignal: lastDateData?.most_common_gsm_signal || 0,
            };

            setSummaryData(processedSummary);
          } else {
            // No data available, set defaults
            setSummaryData({
              distance: 0,
              duration: 0,
              stops: 0,
              maxSpeed: 0,
              avgSpeed: 0,
              ignitionHours: 0,
              idleTime: 0,
              stopTime: 0,
              chargingTime: 0,
              gpsTrackingTime: 0,
              startOdometer: 0,
              endOdometer: 0,
              startPosition: "",
              endPosition: "",
              currentStatus: "",
              batteryLevel: 0,
              avgBatteryLevel: 0,
              voltageLevel: 0,
              gsmSignal: 0,
            });
          }
        } else {
          // No device data found, set defaults
          setSummaryData({
            distance: 0,
            duration: 0,
            stops: 0,
            maxSpeed: 0,
            avgSpeed: 0,
            ignitionHours: 0,
            idleTime: 0,
            stopTime: 0,
            chargingTime: 0,
            gpsTrackingTime: 0,
            startOdometer: 0,
            endOdometer: 0,
            startPosition: "",
            endPosition: "",
            currentStatus: "",
            batteryLevel: 0,
            avgBatteryLevel: 0,
            voltageLevel: 0,
            gsmSignal: 0,
          });
        }
      } else {
        // No response data, set defaults
        setSummaryData({
          distance: 0,
          duration: 0,
          stops: 0,
          maxSpeed: 0,
          avgSpeed: 0,
          ignitionHours: 0,
          idleTime: 0,
          stopTime: 0,
          chargingTime: 0,
          gpsTrackingTime: 0,
          startOdometer: 0,
          endOdometer: 0,
          startPosition: "",
          endPosition: "",
          currentStatus: "",
          batteryLevel: 0,
          avgBatteryLevel: 0,
          voltageLevel: 0,
          gsmSignal: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Initial data fetch - only on mount or when id changes
  useEffect(() => {
    if (id) {
      // Use initial startDate and endDate values for initial load
      const initialStartDate = (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
      })();
      const initialEndDate = new Date();
      fetchDataWithDates(initialStartDate, initialEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only depend on id, not on date changes

  const getFlagIcon = useCallback((color: string) => {
    const svg = `
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(8,4)">
          <path d="M0 0h2v24H0z" fill="${color}"/>
          <path d="M2 2h12l-4 5 4 5H2z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
        </g>
      </svg>
    `;

    if (
      typeof window !== "undefined" &&
      window.google &&
      window.google.maps &&
      typeof window.google.maps.Size === "function" &&
      typeof window.google.maps.Point === "function"
    ) {
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(8, 30),
      };
    }

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    };
  }, []);

  const getStopIcon = useCallback(() => {
    const svg = `
      <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="stopGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#FFCDD2"/>
            <stop offset="100%" stop-color="#E53935"/>
          </radialGradient>
        </defs>
        <circle cx="14" cy="14" r="12" fill="url(#stopGradient)" stroke="#ffffff" stroke-width="2"/>
        <path d="M9 9l10 10M19 9l-10 10" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;

    if (
      typeof window !== "undefined" &&
      window.google &&
      window.google.maps &&
      typeof window.google.maps.Size === "function" &&
      typeof window.google.maps.Point === "function"
    ) {
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(28, 28),
        anchor: new window.google.maps.Point(14, 14),
      };
    }

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    };
  }, []);

  // Initialize Google Maps similar to VehicleTracking
  const initializeMap = () => {
    if (
      !mapRef.current ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Map ||
      isMapInitialized ||
      historyData.length === 0
    )
      return;

    const { latitude, longitude } = historyData[0]; // Start from the beginning (oldest position)

    const mapOptions = {
      center: { lat: Number(latitude), lng: Number(longitude) },
      zoom: mapZoom,
      mapTypeId: mapType,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
    };

    googleMapRef.current = new window.google.maps.Map(
      mapRef.current,
      mapOptions
    );

    // Add traffic layer
    if (showTraffic) {
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
      trafficLayerRef.current.setMap(googleMapRef.current);
    }

    // Create polyline for route
    const pathPoints = historyData.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    }));

    polylineRef.current = new window.google.maps.Polyline({
      path: pathPoints,
      geodesic: true,
      strokeColor: theme.palette.primary.main,
      strokeOpacity: 0.7,
      strokeWeight: 4,
    });
    polylineRef.current.setMap(googleMapRef.current);

    if (historyData.length > 0) {
      const startPoint = historyData[0];
      const endPoint = historyData[historyData.length - 1];

      if (startMarkerRef.current) {
        startMarkerRef.current.setMap(null);
      }
      startMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: startPoint.latitude,
          lng: startPoint.longitude,
        },
        map: googleMapRef.current,
        title: "Start",
        icon: getFlagIcon("#43A047"),
        zIndex: 1100,
      });

      if (endMarkerRef.current) {
        endMarkerRef.current.setMap(null);
      }
      endMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: endPoint.latitude,
          lng: endPoint.longitude,
        },
        map: googleMapRef.current,
        title: "End",
        icon: getFlagIcon("#E53935"),
        zIndex: 1100,
      });

      const currentPos = historyData[currentIndex] || historyData[historyData.length - 1];
      vehicleMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: currentPos.latitude,
          lng: currentPos.longitude,
        },
        map: googleMapRef.current,
        title: "Current Position",
        icon: {
          url: currentPos.speed === 0 ? "/car/red.png" : "/car/green.png",
          scaledSize: new window.google.maps.Size(30, 30),
          anchor: new window.google.maps.Point(15, 15),
        },
        zIndex: 1000,
      });

      // Apply rotation using CSS transform
      if (vehicleMarkerRef.current) {
        const rotation = (currentPos.course || 0) + 90;
        const markerElement = vehicleMarkerRef.current.getIcon();
        setTimeout(() => {
          const img = document.querySelector(`img[src="${markerElement.url}"]`) as HTMLImageElement;
          if (img) {
            img.style.transform = `rotate(${rotation}deg)`;
            img.style.transformOrigin = 'center';
          }
        }, 100);
      }
    }

    // Add stop markers
    updateStopMarkers();

    setIsMapInitialized(true);
  };

  // Load Google Maps API
  const loadGoogleMapsAPI = () => {
    // Check if Google Maps API is already loaded and ready
    if (window.google && window.google.maps && window.google.maps.Map) {
      initializeMap();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener("load", () => {
        // Wait a bit more to ensure maps API is fully initialized
        setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            initializeMap();
          }
        }, 100);
      });
      return;
    }

    // Load the script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait a bit to ensure maps API is fully initialized
      setTimeout(() => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          initializeMap();
        }
      }, 100);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps API");
    };
    document.head.appendChild(script);
  };

  // Update stop markers
  const updateStopMarkers = () => {
    if (
      !googleMapRef.current ||
      !showStops ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Marker
    )
      return;

    // Clear existing stop markers
    stopMarkersRef.current.forEach((marker) => marker.setMap(null));
    stopMarkersRef.current = [];

    // Add new stop markers
    stopLocations.forEach((stop, index) => {
      const marker = new window.google.maps.Marker({
        position: {
          lat: stop.latitude,
          lng: stop.longitude,
        },
        map: googleMapRef.current,
        title: `Stop #${index + 1} - Duration: ${moment
          .duration(stop.duration)
          .humanize()}`,
        // icon: getStopIcon(),
        // zIndex: 1050,
      });
      stopMarkersRef.current.push(marker);
    });
  };

  // Update existing map with new route data
  const updateMapWithNewData = useCallback(() => {
    if (
      !isMapInitialized ||
      !googleMapRef.current ||
      historyData.length === 0 ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Polyline ||
      !window.google.maps.Marker
    )
      return;

    // Clear existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }

    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.setMap(null);
    }

    // Clear stop markers
    stopMarkersRef.current.forEach((marker) => marker.setMap(null));
    stopMarkersRef.current = [];

    if (startMarkerRef.current) {
      startMarkerRef.current.setMap(null);
      startMarkerRef.current = null;
    }

    if (endMarkerRef.current) {
      endMarkerRef.current.setMap(null);
      endMarkerRef.current = null;
    }

    // Create new polyline with updated route
    const pathPoints = historyData.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    }));

    polylineRef.current = new window.google.maps.Polyline({
      path: pathPoints,
      geodesic: true,
      strokeColor: theme.palette.primary.main,
      strokeOpacity: 0.7,
      strokeWeight: 4,
    });
    polylineRef.current.setMap(googleMapRef.current);

    const currentPos = historyData[currentIndex] || historyData[historyData.length - 1];
    vehicleMarkerRef.current = new window.google.maps.Marker({
      position: {
        lat: currentPos.latitude,
        lng: currentPos.longitude,
      },
      map: googleMapRef.current,
      title: "Current Position",
      icon: {
        url: currentPos.speed === 0 ? "/car/red.png" : "/car/green.png",
        scaledSize: new window.google.maps.Size(30, 30),
        anchor: new window.google.maps.Point(15, 15),
      },
      zIndex: 1000,
    });

    // Apply rotation using CSS transform
    if (vehicleMarkerRef.current) {
      const rotation = (currentPos.course || 0) + 90;
      const markerElement = vehicleMarkerRef.current.getIcon();
      setTimeout(() => {
        const img = document.querySelector(`img[src="${markerElement.url}"]`) as HTMLImageElement;
        if (img) {
          img.style.transform = `rotate(${rotation}deg)`;
          img.style.transformOrigin = 'center';
        }
      }, 100);
    }

    if (historyData.length > 0) {
      const startPoint = historyData[0];
      const endPoint = historyData[historyData.length - 1];

      startMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: startPoint.latitude,
          lng: startPoint.longitude,
        },
        map: googleMapRef.current,
        title: "Start",
        icon: getFlagIcon("#43A047"),
        zIndex: 1100,
      });

      endMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: endPoint.latitude,
          lng: endPoint.longitude,
        },
        map: googleMapRef.current,
        title: "End",
        icon: getFlagIcon("#E53935"),
        zIndex: 1100,
      });
    }

    // Update stop markers
    updateStopMarkers();

    // Center map on the new route
    if (historyData.length > 0 && window.google.maps.LatLngBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      historyData.forEach((point) => {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });
      googleMapRef.current.fitBounds(bounds);
    }
  }, [historyData, theme.palette.primary.main, isMapInitialized]);

  // Track previous historyData to detect actual data changes
  const prevHistoryDataRef = useRef<HistoryPoint[]>([]);

  // Update map when history data changes
  useEffect(() => {
    if (historyData.length > 0 && !isMapInitialized) {
      loadGoogleMapsAPI();
    } else if (historyData.length > 0 && isMapInitialized) {
      // Only update map if we have genuinely new data (not just a state update)
      const hasNewData =
        prevHistoryDataRef.current.length !== historyData.length ||
        (historyData.length > 0 &&
          prevHistoryDataRef.current.length > 0 &&
          prevHistoryDataRef.current[0].timestamp.getTime() !==
          historyData[0].timestamp.getTime());

      if (hasNewData) {
        // Update existing map with new data
        updateMapWithNewData();
        // Reset playback for new data - start from beginning (oldest position)
        setCurrentIndex(0);
        setPlaying(false);
        // Update ref
        prevHistoryDataRef.current = [...historyData];
      }
    }
  }, [historyData, isMapInitialized, updateMapWithNewData]);

  // Update traffic layer
  useEffect(() => {
    if (
      !isMapInitialized ||
      !googleMapRef.current ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.TrafficLayer
    )
      return;

    if (showTraffic) {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new window.google.maps.TrafficLayer();
      }
      trafficLayerRef.current.setMap(googleMapRef.current);
    } else {
      if (trafficLayerRef.current) {
        trafficLayerRef.current.setMap(null);
      }
    }
  }, [showTraffic, isMapInitialized]);

  // Update map type
  useEffect(() => {
    if (isMapInitialized && googleMapRef.current) {
      googleMapRef.current.setMapTypeId(mapType);
    }
  }, [mapType, isMapInitialized]);

  // Update stop markers when showStops changes
  useEffect(() => {
    if (isMapInitialized) {
      updateStopMarkers();
    }
  }, [showStops, stopLocations, isMapInitialized]);

  // Playback controls
  useEffect(() => {
    if (playing && historyData.length > 0) {
      // Clear any existing interval
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }

      // Set new interval based on playback speed
      playbackIntervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= historyData.length - 1) {
            setPlaying(false);
            return prevIndex;
          }
          return prevIndex + 1;
        });
      }, 1000 / playbackSpeed);
    } else if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [playing, playbackSpeed, historyData]);

  // Update vehicle marker position when current index changes
  useEffect(() => {
    if (
      !isMapInitialized ||
      !vehicleMarkerRef.current ||
      !historyData[currentIndex] ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Size ||
      !window.google.maps.Point
    )
      return;

    const { latitude, longitude } = historyData[currentIndex];
    const newPosition = { lat: latitude, lng: longitude };

    vehicleMarkerRef.current.setPosition(newPosition);

    const currentPoint = historyData[currentIndex];
    const iconUrl = currentPoint.speed === 0 ? "/car/red.png" : "/car/green.png";
    vehicleMarkerRef.current.setIcon({
      url: iconUrl,
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: new window.google.maps.Point(15, 15),
    });

    // Apply rotation using CSS transform
    const rotation = (currentPoint.course || 0) + 360;
    setTimeout(() => {
      const img = document.querySelector(`img[src="${iconUrl}"]`) as HTMLImageElement;
      if (img) {
        img.style.transform = `rotate(${rotation}deg)`;
        img.style.transformOrigin = 'center';
      }
    }, 50);

    // Center map on current position
    if (googleMapRef.current) {
      googleMapRef.current.panTo(newPosition);
    }
  }, [currentIndex, historyData, isMapInitialized]);

  const handlePlay = () => {
    if (!playing) {
      // When starting playback, always start from the beginning (oldest position)
      setCurrentIndex(0);
    }
    setPlaying(!playing);
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
  };

  const jumpToStart = () => {
    setCurrentIndex(0);
    setPlaying(false);
  };

  const jumpToEnd = () => {
    setCurrentIndex(historyData.length - 1);
    setPlaying(false);
  };

  const step = (direction: "forward" | "backward") => {
    setPlaying(false);
    if (direction === "forward" && currentIndex < historyData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (direction === "backward" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setCurrentIndex(newValue as number);
  };

  // Use all historyData for display - filtering by date is already done by API
  const filteredHistory = historyData;

  // Calculate path for polyline - Memoize route coordinates with sampling
  const pathPoints = React.useMemo(() => {
    if (!filteredHistory.length) return [];

    const samplingRate = Math.max(
      1,
      Math.floor(filteredHistory.length / MAX_POINTS)
    );
    return filteredHistory
      .filter((_, index) => index % samplingRate === 0)
      .map((point) => ({
        lat: point.latitude,
        lng: point.longitude,
      }));
  }, [filteredHistory]);

  // Calculate distance using Haversine formula
  const calculateDistance = (points: HistoryPoint[]): number => {
    if (points.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 1; i < points.length; i++) {
      const prevPoint = points[i - 1];
      const currentPoint = points[i];

      // Haversine formula to calculate distance between coordinates
      const R = 6371; // Earth radius in km
      const dLat =
        ((currentPoint.latitude - prevPoint.latitude) * Math.PI) / 180;
      const dLon =
        ((currentPoint.longitude - prevPoint.longitude) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((prevPoint.latitude * Math.PI) / 180) *
        Math.cos((currentPoint.latitude * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      totalDistance += distance;
    }

    return totalDistance;
  };

  // Use API summary data instead of calculated values
  const totalDistance = summaryData?.distance ? summaryData.distance / 1000 : 0; // Convert to km
  const avgSpeed = summaryData?.avgSpeed || 0;
  const maxSpeed = summaryData?.maxSpeed || 0;
  // const drivingTime = summaryData?.duration
  // Current point details
  const currentPoint = filteredHistory[currentIndex];
  
  // Debug: Log current point data to see what's available
  useEffect(() => {
    if (currentPoint) {
      console.log("Current Point Data:", currentPoint);
      console.log("Location:", currentPoint.location);
      console.log("Address:", currentPoint.location?.address);
      console.log("Created At:", currentPoint.created_at);
      console.log("Fix Time:", currentPoint.fix_time);
    }
  }, [currentPoint]);

  // Apply filters
  const handleApplyFilters = async () => {
    // Validate dates before applying
    if (!startDate || !endDate) {
      console.error("Start date and end date are required");
      return;
    }
    
    if (startDate > endDate) {
      console.error("Start date cannot be after end date");
      return;
    }
    
    setFilterOpen(false);
    // Use fetchDataWithDates with current state values to avoid closure issues
    await fetchDataWithDates(startDate, endDate);
  };

  // Clear filters
  const handleClearFilters = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const now = new Date();
    
    setStartDate(today);
    setEndDate(now);
    
    // Use fetchDataWithDates with the new dates directly
    await fetchDataWithDates(today, now);
  };

  // Handle date changes with validation
  const handleStartDateChange = (newDate: Date | null) => {
    if (newDate && newDate > new Date()) {
      // Prevent future dates
      return;
    }
    setStartDate(newDate);
  };

  const handleEndDateChange = (newDate: Date | null) => {
    if (newDate && newDate > new Date()) {
      // Prevent future dates
      return;
    }
    setEndDate(newDate);
  };

  // Add handleBack function
  const handleBack = () => {
    navigate(-1);
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Google Maps API key is not configured. Please add
          REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables.
        </Alert>
      </Box>
    );
  }

  if (loading && historyData.length === 0) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <CircularProgress sx={{ color: "#4299e1" }} />
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            color: "#4a5568",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Loading history data...
        </Typography>
      </Box>
    );
  }

  // Minimal styling to prevent scrolling
  const minimalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
    
    * {
      box-sizing: border-box;
    }
    
    body, html {
      margin: 0;
      padding: 0;
      height: 100%;
      overflow: hidden;
      position: fixed;
      width: 100%;
    }
    
    #root {
      height: 100vh;
      max-height: 100vh;
      overflow: hidden;
      position: relative;
    }
    
    .App {
      height: 100vh;
      max-height: 100vh;
      overflow: hidden;
    }
    
    .history-playback-container {
      font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      height: 100vh;
      max-height: 100vh;
      display: flex;
      flex-direction: column;
      background: #f8fafc;
      position: relative;
      overflow: hidden;
      min-height: 0;
      flex-shrink: 0;
    }
    
    .map-container {
      height: calc(100vh - 190px);
      overflow: hidden;
      min-height: 0;
    }
  `;

  return (
    <>
      <style>{minimalStyles}</style>
      <div className="history-playback-container">
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "white",
            borderBottom: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            minHeight: "56px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                size="small"
                sx={{
                  bgcolor: "#f7fafc",
                  border: "1px solid #e2e8f0",
                  "&:hover": {
                    bgcolor: "#edf2f7",
                    borderColor: "#cbd5e0",
                  },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "#1a202c",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              History Playback - {vehicleName}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Filter history data">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterIcon fontSize="small" />}
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#4a5568",
                  fontSize: "0.875rem",
                  "&:hover": {
                    bgcolor: "#f7fafc",
                    borderColor: "#cbd5e0",
                  },
                }}
              >
                Filters
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {filterOpen && (
          <Paper sx={{ p: 2, mb: 2, mx: 2 }}>
            {loading && (
              <LinearProgress
                sx={{
                  mb: 2,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "#4299e1",
                  },
                }}
              />
            )}
            <StyledGrid
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              }}
            >
              <Box>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Start Date & Time"
                    value={startDate}
                    onChange={handleStartDateChange}
                    maxDate={endDate || undefined}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="End Date & Time"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={startDate || undefined}
                    maxDate={new Date()}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Playback Speed</InputLabel>
                  <Select
                    value={playbackSpeed}
                    label="Playback Speed"
                    onChange={(e) =>
                      handleSpeedChange(e.target.value as number)
                    }
                  >
                    {speeds.map((speed) => (
                      <MenuItem key={speed} value={speed}>
                        {speed}x
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Tooltip title="Clear all filters">
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                </Tooltip>
                <Tooltip title="Apply selected filters">
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={handleApplyFilters}
                    disabled={loading}
                  >
                    {loading ? "Applying..." : "Apply Filters"}
                  </Button>
                </Tooltip>
              </Box>
            </StyledGrid>
          </Paper>
        )}

        {/* Main content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 64px)",
            m: 0,
            p: 0,
          }}
        >
          {/* Map view */}
          <Box
            className="map-container"
            sx={{
              flex: 1,
              position: "relative",
            }}
          >
            {historyData.length > 0 ? (
              <div
                ref={mapRef}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {loading
                    ? "Loading history data..."
                    : "No route data available for the selected date range"}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                zIndex: 999,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                bgcolor: "white",
                borderRadius: 2,
                p: 1,
                boxShadow: theme.shadows[3],
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tooltip title="Toggle Traffic Layer" placement="right">
                <IconButton
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "grey.100" } }}
                  onClick={() => setShowTraffic(!showTraffic)}
                >
                  <TrafficIcon color={showTraffic ? "primary" : "disabled"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Terrain View" placement="right">
                <IconButton
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "grey.100" } }}
                  onClick={() => setMapType("terrain")}
                >
                  <TerrainIcon
                    color={mapType === "terrain" ? "primary" : "disabled"}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Satellite View" placement="right">
                <IconButton
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "grey.100" } }}
                  onClick={() => setMapType("satellite")}
                >
                  <SatelliteIcon
                    color={mapType === "satellite" ? "primary" : "disabled"}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Road Map View" placement="right">
                <IconButton
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "grey.100" } }}
                  onClick={() => setMapType("roadmap")}
                >
                  <MapIcon
                    color={mapType === "roadmap" ? "primary" : "disabled"}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Toggle Stop Points" placement="right">
                <IconButton
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "grey.100" } }}
                  onClick={() => setShowStops(!showStops)}
                >
                  {showStops ? (
                    <VisibilityIcon color="primary" />
                  ) : (
                    <VisibilityOffIcon color="disabled" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 999,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: 220,
              }}
            >
              <Card sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}>
                <CardContent sx={{ py: 1, px: 2, "&:last-child": { pb: 1 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Distance
                  </Typography>
                  <Typography variant="h6">
                    {totalDistance.toFixed(2)} km
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}>
                <CardContent sx={{ py: 1, px: 2, "&:last-child": { pb: 1 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Max Speed
                  </Typography>
                  <Typography variant="h6">{maxSpeed} km/h</Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}>
                <CardContent sx={{ py: 1, px: 2, "&:last-child": { pb: 1 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Avg Speed
                  </Typography>
                  <Typography variant="h6">
                    {avgSpeed.toFixed(1)} km/h
                  </Typography>
                </CardContent>
              </Card>
{/* 
              <Card sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}>
                <CardContent sx={{ py: 1, px: 2, "&:last-child": { pb: 1 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Driving Time
                  </Typography>
                  <Typography variant="h6">
                    {drivingTime?.toFixed(1) ?? '0.0'} hours
                  </Typography>
                </CardContent>
              </Card> */}
            </Box>
          </Box>

          {/* Redesigned Control Panel */}
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.98)",
              borderTop: "1px solid #e2e8f0",
              backdropFilter: "blur(8px)",
              p: 2,
              position: "absolute",
              bottom: 85,
              left: 0,
              right: 0,
              width: "100%"
            }}
          >
            <Box
              sx={{
                maxWidth: "100%",
                mx: "auto",
              }}
            >
            {/* Current Point Information */}
            {currentPoint && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(6, 1fr)",
                  },
                  gap: 2,
                  mb: 2,
                  p: 2,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Start
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#1a202c",
                      mt: 0.5,
                    }}
                  >
                    {moment(startDate).format("DD/MM/YYYY hh:mm A")}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    End
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#1a202c",
                      mt: 0.5,
                    }}
                  >
                    {moment(endDate).format("DD/MM/YYYY hh:mm A")}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Date Time
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#1a202c",
                      mt: 0.5,
                    }}
                  >
                    {moment(currentPoint?.created_at || currentPoint?.fix_time).format(
                      "DD/MM/YYYY hh:mm A"
                    )}
                  </Typography>
                </Box>

                <Box sx={{ 
                  textAlign: "center", 
                  gridColumn: { xs: "1 / -1", md: "auto" },
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Location
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#1a202c",
                      mt: 0.5,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.4,
                    }}
                    title={currentPoint?.location?.address || `Coordinates: ${currentPoint?.latitude}, ${currentPoint?.longitude}`}
                  >
                    {currentPoint?.location?.address || `Coordinates: ${currentPoint?.latitude}, ${currentPoint?.longitude}`}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Speed
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: currentPoint.speed > 0 ? "#48bb78" : "#e53e3e",
                      mt: 0.5,
                    }}
                  >
                    {currentPoint.speed} km/h
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#4a5568",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Status
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: currentPoint.status === "moving" ? "#48bb78" : "#ed8936",
                      mt: 0.5,
                      textTransform: "capitalize",
                    }}
                  >
                    {currentPoint.status}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Playback Controls */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {/* Progress Slider */}
              <Box sx={{ flex: 1, minWidth: "300px" }}>
                <Slider
                  min={0}
                  max={filteredHistory.length > 0 ? filteredHistory.length - 1 : 0}
                  value={currentIndex}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => {
                    if (filteredHistory[value]) {
                      return moment(filteredHistory[value].timestamp).format("HH:mm");
                    }
                    return "";
                  }}
                  sx={{
                    "& .MuiSlider-thumb": {
                      width: 16,
                      height: 16,
                    },
                    "& .MuiSlider-track": {
                      height: 4,
                    },
                    "& .MuiSlider-rail": {
                      height: 4,
                    },
                  }}
                />
              </Box>

              {/* Control Buttons */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  p: 0.5,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Tooltip title="Jump to Start">
                  <IconButton
                    onClick={jumpToStart}
                    disabled={playing || currentIndex === 0}
                    size="small"
                    sx={{
                      "&:hover": { bgcolor: "#edf2f7" },
                      "&:disabled": { color: "#cbd5e0" },
                    }}
                  >
                    <SkipPreviousIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Step Backward">
                  <IconButton
                    onClick={() => step("backward")}
                    disabled={playing || currentIndex === 0}
                    size="small"
                    sx={{
                      "&:hover": { bgcolor: "#edf2f7" },
                      "&:disabled": { color: "#cbd5e0" },
                    }}
                  >
                    <FastRewindIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={playing ? "Pause" : "Play"}>
                  <IconButton
                    onClick={handlePlay}
                    sx={{
                      bgcolor: playing ? "#4299e1" : "#ffffff",
                      color: playing ? "white" : "#4a5568",
                      "&:hover": {
                        bgcolor: playing ? "#3182ce" : "#edf2f7",
                      },
                      mx: 0.5,
                    }}
                  >
                    {playing ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Step Forward">
                  <IconButton
                    onClick={() => step("forward")}
                    disabled={playing || currentIndex === filteredHistory.length - 1}
                    size="small"
                    sx={{
                      "&:hover": { bgcolor: "#edf2f7" },
                      "&:disabled": { color: "#cbd5e0" },
                    }}
                  >
                    <FastForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Jump to End">
                  <IconButton
                    onClick={jumpToEnd}
                    disabled={playing || currentIndex === filteredHistory.length - 1}
                    size="small"
                    sx={{
                      "&:hover": { bgcolor: "#edf2f7" },
                      "&:disabled": { color: "#cbd5e0" },
                    }}
                  >
                    <SkipNextIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#4299e1" }} size={60} />
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
            }}
          >
            Loading history data...
          </Typography>
        </Box>
      </Backdrop>
    </div>
    </>
  );
};

export default HistoryPlayback;
